import { motion } from "framer-motion";
import {
  Card,
  Button as AntButton,
  Typography,
  Tag,
  Row,
  Col,
  Space,
  Tooltip,
  InputNumber,
  Checkbox,
  Divider,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  EditOutlined,
  SaveOutlined,
  PlusOutlined,
  FileTextOutlined,
  LockOutlined,
} from "@ant-design/icons";
import { useState } from "react";
import TmCountSuggestion from "./TmCountSuggestion";
import { useGstRate } from "../../hooks/useTaxRates";

const { Text, Title } = Typography;

// Thousands separators on the integer part only. The naive
// /\B(?=(\d{3})+(?!\d))/g applied to the whole string also groups the decimals
// ("1234.5678" → "1,234.5,678"), so split on the decimal point first.
const groupAmount = (value) => {
  const [intPart = "", decPart] = `${value ?? ""}`.split(".");
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return decPart !== undefined ? `${grouped}.${decPart}` : grouped;
};

// Strip ₹ and grouping commas, keep the decimal point
const parseAmount = (value) => `${value ?? ""}`.replace(/₹\s?|,/g, "");

// Money display: always 2 decimals so ₹25.5 doesn't read as ₹25.5
const money = (value) =>
  Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const OrderSummary = ({
  orderSummary,
  formData,
  isEditing,
  isEditingAmount,
  editablePlanAmount,
  setEditablePlanAmount,
  handleEditAmount,
  handleSaveAmount,
  setIsEditingAmount,
  safeNumber,
  handleGSTCheckboxChange,
  onSaveTMCount,
  onApplyTmSuggestion,
  isSubmitting,
  formRef,
  // Edit mode attorney props
  existingAttorneys = [],
  emptySlots = [],
}) => {
  /* The live rate from the tax master, for the label and the toggle's
     availability. The amount itself is computed upstream from the same rate. */
  const gst = useGstRate();

  // Total TM Count is the one editable TM figure — the per-attorney counts in
  // Attorney Numbers Management show the API values read-only. Saving here sets
  // isTMCountManuallySet upstream so recalculations keep the override.
  const [isEditingTMCount, setIsEditingTMCount] = useState(false);
  const [editableTMCount, setEditableTMCount] = useState(
    Number(orderSummary?.totalTMCount || 0),
  );

  /* The negotiated deal total the rep is aiming for, when there is one. Empty is
     the normal case and means the per-TM price stands on its own — count × price
     is the total, and nothing is being solved backwards from it. */
  const [targetTotal, setTargetTotal] = useState(null);

  // Build combined attorney list for edit mode display
  const editModeAttorneyList = isEditing
    ? [
        ...existingAttorneys.map((a) => ({
          attorneyNumber: a.attorney_number,
          tmCount: Number(a.tm_count || 0),
          status: "valid",
          isExisting: true,
        })),
        ...emptySlots
          .filter((s) => s.status === "valid" && s.value.trim())
          .map((s) => ({
            attorneyNumber: s.value.trim(),
            tmCount: Number(s.tmCount || 0),
            status: "valid",
            isExisting: false,
          })),
      ]
    : [];

  // Decide which attorney list to render
  const displayAttorneyList = isEditing
    ? editModeAttorneyList
    : orderSummary.attorneyDetails || [];

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      style={{ position: "sticky", top: "90px", zIndex: 10 }}
    >
      <Card className="shadow-lg border-0" style={{ borderRadius: 12 }}>
        {/* Header */}
        <div
          className="text-white p-3"
          style={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
          }}
        >
          <Title level={4} className="mb-0 text-white">
            <FileTextOutlined className="me-2" />
            Subscription Summary
          </Title>
        </div>

        <div className="p-3">
          {/* Customer Details Section */}
          <Card
            size="small"
            className="mb-3"
            style={{ backgroundColor: "#f0f5ff", borderRadius: 8 }}
          >
            <Title
              level={5}
              className="mb-3"
              style={{ fontSize: 14, color: "#1890ff" }}
            >
              <InfoCircleOutlined className="me-2" />
              Customer Details
            </Title>
            <Row gutter={[8, 12]}>
              <Col span={8}>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Name
                </Text>
              </Col>
              <Col span={16}>
                <Text strong style={{ fontSize: 13 }}>
                  {orderSummary.customerName}
                </Text>
              </Col>

              <Col span={8}>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Email
                </Text>
              </Col>
              <Col span={16}>
                <Text style={{ fontSize: 12 }}>
                  {orderSummary.customerEmail}
                </Text>
              </Col>

              <Col span={8}>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Type
                </Text>
              </Col>
              <Col span={16}>
                <Tag
                  color={
                    orderSummary.customerType === "Paid"
                      ? "green"
                      : orderSummary.customerType === "Free Trial"
                        ? "blue"
                        : "default"
                  }
                >
                  {orderSummary.customerType}
                </Tag>
              </Col>
            </Row>
          </Card>

          {/* Plan Details Section */}
          <Card
            size="small"
            className="mb-3"
            style={{ backgroundColor: "#fff7e6", borderRadius: 8 }}
          >
            <Title
              level={5}
              className="mb-3"
              style={{ fontSize: 14, color: "#fa8c16" }}
            >
              <FileTextOutlined className="me-2" />
              Plan Details
            </Title>
            <Row gutter={[8, 12]}>
              <Col span={10}>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Plan Name
                </Text>
              </Col>
              <Col span={14}>
                <Text strong style={{ fontSize: 13 }}>
                  {orderSummary.planName}
                </Text>
              </Col>

              {formData.customer_type === "1" && (
                <>
                  <Col span={10}>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      Plan Duration
                    </Text>
                  </Col>
                  <Col span={14}>
                    <Text strong style={{ fontSize: 13 }}>
                      {orderSummary.planType}
                    </Text>
                  </Col>
                </>
              )}

              {formData.customer_type === "2" && (
                <>
                  <Col span={10}>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      End Date
                    </Text>
                  </Col>
                  <Col span={14}>
                    <Text style={{ fontSize: 12 }}>
                      {orderSummary.end_date}
                    </Text>
                  </Col>

                  {orderSummary.trialDuration != null && (
                    <>
                      <Col span={10}>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          Duration
                        </Text>
                      </Col>
                      <Col span={14}>
                        <Tag color="purple">
                          {orderSummary.trialDuration} day
                          {orderSummary.trialDuration !== 1 ? "s" : ""}
                        </Tag>
                      </Col>
                    </>
                  )}
                </>
              )}
            </Row>
          </Card>

          {/* Payment Details Section - Only for Paid */}
          {formData.customer_type === "1" && (
            <Card
              size="small"
              className="mb-3"
              style={{
                backgroundColor: "#f6ffed",
                borderRadius: 8,
                border: "1px solid #b7eb8f",
              }}
            >
              <Title
                level={5}
                className="mb-3"
                style={{ fontSize: 14, color: "#52c41a" }}
              >
                <InfoCircleOutlined className="me-2" />
                Payment Details
              </Title>

              <Row gutter={[8, 12]}>
                {/* Payment Status */}
                <Col span={10}>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    Payment Status
                  </Text>
                </Col>
                <Col span={14}>
                  <Tag
                    color={
                      formData.payment_status === "paid" ? "success" : "warning"
                    }
                  >
                    {formData.payment_status === "paid" ? "Paid" : "Unpaid"}
                  </Tag>
                </Col>

                {/* Show only if Paid */}
                {formData.payment_status === "paid" && (
                  <>
                    <Col span={10}>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        Payment Mode
                      </Text>
                    </Col>
                    <Col span={14}>
                      <Text strong style={{ fontSize: 13 }}>
                        {formData.payment_mode || "-"}
                      </Text>
                    </Col>

                    <Col span={10}>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        UTR No
                      </Text>
                    </Col>
                    <Col span={14}>
                      <Text
                        strong
                        style={{
                          fontSize: 13,
                          color: "#389e0d",
                        }}
                      >
                        {formData.utr_no || "-"}
                      </Text>
                    </Col>

                    <Col span={10}>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        Attachment
                      </Text>
                    </Col>
                    <Col span={14}>
                      <Tag
                        color={
                          formData.payment_attachment ? "green" : "default"
                        }
                      >
                        {formData.payment_attachment
                          ? "Uploaded"
                          : "Not Uploaded"}
                      </Tag>
                    </Col>
                  </>
                )}
              </Row>
            </Card>
          )}

          {/* Attorney TM Breakdown Section */}
          {displayAttorneyList.length > 0 && (
            <Card
              size="small"
              className="mb-3"
              style={{
                backgroundColor: "#e6f7ff",
                borderRadius: 8,
                border: "2px solid #91d5ff",
              }}
            >
              <div className="d-flex align-items-center justify-content-between mb-3">
                <Title
                  level={5}
                  className="mb-0"
                  style={{ fontSize: 14, color: "#1890ff" }}
                >
                  <InfoCircleOutlined className="me-2" />
                  Attorney TM Breakdown
                </Title>
                {isEditing && (
                  <Space size={4}>
                    {existingAttorneys.length > 0 && (
                      <Tag
                        color="blue"
                        style={{ fontSize: 10, margin: 0 }}
                      >
                        {existingAttorneys.length} Existing
                      </Tag>
                    )}
                    {emptySlots.filter(
                      (s) => s.status === "valid" && s.value.trim(),
                    ).length > 0 && (
                      <Tag
                        color="purple"
                        style={{ fontSize: 10, margin: 0 }}
                      >
                        {
                          emptySlots.filter(
                            (s) => s.status === "valid" && s.value.trim(),
                          ).length
                        }{" "}
                        New
                      </Tag>
                    )}
                  </Space>
                )}
              </div>
              <div
                style={{
                  maxHeight: 180,
                  overflowY: "auto",
                  paddingRight: 4,
                }}
              >
                {displayAttorneyList.map((attorney, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{
                      duration: 0.2,
                      delay: idx * 0.05,
                    }}
                  >
                    <Row
                      className="mb-2 p-2"
                      gutter={8}
                      align="middle"
                      style={{
                        backgroundColor:
                          attorney.status === "valid" ? "#f6ffed" : "#fff1f0",
                        borderRadius: 6,
                        border: `1px solid ${attorney.status === "valid" ? "#b7eb8f" : "#ffccc7"}`,
                      }}
                    >
                      <Col span={14}>
                        <Space size={4} direction="vertical">
                          <div className="d-flex align-items-center" style={{ gap: 4 }}>
                            <Text
                              style={{
                                fontSize: 11,
                                color: "#8c8c8c",
                              }}
                            >
                              Attorney #{attorney.attorneyNumber}
                            </Text>
                            {/* Show Locked/New tag in edit mode */}
                            {isEditing && attorney.isExisting && (
                              <Tag
                                color="default"
                                icon={<LockOutlined />}
                                style={{
                                  margin: 0,
                                  fontSize: 9,
                                  padding: "0 4px",
                                  lineHeight: "14px",
                                }}
                              >
                                Locked
                              </Tag>
                            )}
                            {isEditing && !attorney.isExisting && (
                              <Tag
                                color="purple"
                                style={{
                                  margin: 0,
                                  fontSize: 9,
                                  padding: "0 4px",
                                  lineHeight: "14px",
                                }}
                              >
                                New
                              </Tag>
                            )}
                          </div>
                          {attorney.status === "valid" && (
                            <Tag
                              color="success"
                              icon={<CheckCircleOutlined />}
                              style={{
                                margin: 0,
                                fontSize: 10,
                              }}
                            >
                              Valid
                            </Tag>
                          )}
                          {attorney.status === "invalid" && (
                            <Tag
                              color="error"
                              icon={<CloseCircleOutlined />}
                              style={{
                                margin: 0,
                                fontSize: 10,
                              }}
                            >
                              Invalid
                            </Tag>
                          )}
                        </Space>
                      </Col>
                      <Col span={10} className="text-end">
                        <Text
                          strong
                          style={{
                            fontSize: 18,
                            color:
                              attorney.status === "valid"
                                ? "#52c41a"
                                : "#bfbfbf",
                            display: "block",
                            lineHeight: 1.2,
                          }}
                        >
                          {Number(attorney.tmCount || 0).toLocaleString(
                            "en-IN",
                          )}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 10 }}>
                          TM{attorney.tmCount !== 1 ? "s" : ""}
                        </Text>
                      </Col>
                    </Row>
                  </motion.div>
                ))}
              </div>
            </Card>
          )}

          {/* Total TM Count — editable; per-attorney counts are read-only */}
          <Card
            size="small"
            className="mb-3"
            style={{
              background: "linear-gradient(135deg, #f9f0ff 0%, #efdbff 100%)",
              borderColor: "#d3adf7",
              borderWidth: 2,
              borderRadius: 8,
            }}
          >
            <Row justify="space-between" align="middle">
              <Col>
                <Text strong style={{ fontSize: 15, color: "#531dab" }}>
                  Total TM Count
                </Text>
                <br />
                <Text type="secondary" style={{ fontSize: 10 }}>
                  {formData.customer_type === "2"
                    ? "Free trial"
                    : "Sum of all attorneys"}
                </Text>
              </Col>
              <Col className="text-end">
                {isEditingTMCount ? (
                  <Space>
                    <InputNumber
                      size="middle"
                      value={editableTMCount}
                      onChange={(value) => setEditableTMCount(value ?? 0)}
                      min={0}
                      max={10000000}
                      style={{ width: 120 }}
                      formatter={(value) =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                      }
                      parser={(value) => value.replace(/(,*)/g, "")}
                      autoFocus
                    />
                    <Tooltip title="Save TM Count">
                      <AntButton
                        type="primary"
                        size="middle"
                        icon={<SaveOutlined />}
                        onClick={() => {
                          onSaveTMCount &&
                            onSaveTMCount(Math.max(0, Number(editableTMCount) || 0));
                          setIsEditingTMCount(false);
                        }}
                        style={{
                          backgroundColor: "#52c41a",
                          borderColor: "#52c41a",
                        }}
                      />
                    </Tooltip>
                    <Tooltip title="Cancel">
                      <AntButton
                        size="middle"
                        icon={<CloseCircleOutlined />}
                        onClick={() => {
                          setIsEditingTMCount(false);
                          setEditableTMCount(Number(orderSummary.totalTMCount || 0));
                        }}
                        danger
                      />
                    </Tooltip>
                  </Space>
                ) : (
                  <Space>
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 200 }}
                    >
                      <Tag
                        color="purple"
                        style={{
                          fontSize: 22,
                          padding: "8px 20px",
                          fontWeight: 700,
                          borderRadius: 8,
                          marginInlineEnd: 0,
                        }}
                      >
                        {Number(orderSummary.totalTMCount || 0).toLocaleString("en-IN")}
                      </Tag>
                    </motion.div>
                    <Tooltip title="Edit Total TM Count">
                      <AntButton
                        type="link"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => {
                          setEditableTMCount(Number(orderSummary.totalTMCount || 0));
                          setIsEditingTMCount(true);
                        }}
                        style={{ color: "#531dab" }}
                      />
                    </Tooltip>
                  </Space>
                )}
              </Col>
            </Row>

            {/* Target-total helper — paid orders only. Admin types the negotiated
                total; we suggest nearby TM counts whose per-TM price is exact to
                2 decimals, so they never hand-calculate a repeating price. */}
            {formData.customer_type === "1" && (
              <div
                style={{
                  marginTop: 10,
                  paddingTop: 10,
                  borderTop: "1px dashed #d3adf7",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    flexWrap: "wrap",
                  }}
                >
                  <Tooltip title="The negotiated Final Amount before GST. Suggestions below adjust the TM count so the per-TM price divides this total exactly.">
                    <Text
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: "#531dab",
                        borderBottom: "1px dotted #b37feb",
                        cursor: "help",
                      }}
                    >
                      Target total (pre-GST)
                    </Text>
                  </Tooltip>
                  <InputNumber
                    size="small"
                    value={targetTotal}
                    onChange={(value) => setTargetTotal(value)}
                    min={0}
                    max={100000000}
                    step={0.01}
                    precision={2}
                    decimalSeparator="."
                    placeholder={money(orderSummary.finalAmount)}
                    prefix="₹"
                    formatter={groupAmount}
                    parser={parseAmount}
                    style={{ width: 130 }}
                  />
                  {targetTotal != null && (
                    <AntButton
                      type="link"
                      size="small"
                      onClick={() => setTargetTotal(null)}
                      style={{ fontSize: 11, padding: 0, color: "#8c8c8c" }}
                    >
                      Reset
                    </AntButton>
                  )}
                </div>
                {/* Only once a target total is actually typed. Falling back to
                    the base amount meant that setting a per-TM price — a
                    complete instruction on its own — was answered with a warning
                    that the total it happens to produce is not a round number. A
                    price the rep typed is the price; nothing needs solving unless
                    they ask for a total instead. */}
                {targetTotal > 0 && (
                  <TmCountSuggestion
                    amount={targetTotal}
                    tmCount={orderSummary.totalTMCount}
                    onSelect={(newCount, perTm) => {
                      if (onApplyTmSuggestion) onApplyTmSuggestion(newCount, perTm);
                      setTargetTotal(null);
                    }}
                  />
                )}
              </div>
            )}
          </Card>

          <Divider style={{ margin: "12px 0" }} />

          {/*  Price Breakdown - ONLY for Paid (customer_type === "1") */}
          {formData.customer_type === "1" && (
            <div className="mb-3">
              {/* Plan Amount with Edit */}
              <Row justify="space-between" align="middle" className="mb-3">
                <Col>
                  <Text strong style={{ fontSize: 14, color: "#262626" }}>
                    Plan Amount
                  </Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: 10 }}>
                    Per unit price
                  </Text>
                </Col>
                <Col className="text-end">
                  {isEditingAmount ? (
                    <Space>
                      <InputNumber
                        size="middle"
                        value={editablePlanAmount}
                        onChange={(value) => setEditablePlanAmount(value ?? 0)}
                        // Decimal plan amounts are allowed (e.g. ₹25.50)
                        min={0.01}
                        max={10000000}
                        step={0.01}
                        precision={2}
                        decimalSeparator="."
                        style={{ width: 140 }}
                        prefix="₹"
                        formatter={groupAmount}
                        parser={parseAmount}
                        status={editablePlanAmount <= 0 ? "error" : ""}
                        autoFocus
                      />
                      <Tooltip title="Save Amount">
                        <AntButton
                          type="primary"
                          size="middle"
                          icon={<SaveOutlined />}
                          onClick={handleSaveAmount}
                          disabled={editablePlanAmount <= 0}
                          style={{
                            backgroundColor:
                              editablePlanAmount > 0 ? "#52c41a" : "#d9d9d9",
                            borderColor:
                              editablePlanAmount > 0 ? "#52c41a" : "#d9d9d9",
                          }}
                        />
                      </Tooltip>
                      <Tooltip title="Cancel">
                        <AntButton
                          size="middle"
                          icon={<CloseCircleOutlined />}
                          onClick={() => {
                            setIsEditingAmount(false);
                            setEditablePlanAmount(
                              safeNumber(formData.total_amount),
                            );
                          }}
                          danger
                        />
                      </Tooltip>
                    </Space>
                  ) : (
                    <Space>
                      <Text
                        strong
                        style={{
                          fontSize: 18,
                          color:
                            orderSummary.planAmount > 0 ? "#1890ff" : "#ff4d4f",
                        }}
                      >
                        ₹
                        {money(orderSummary.planAmount)}
                      </Text>
                      <Tooltip
                        title={
                          orderSummary.planAmount > 0
                            ? "Edit Amount"
                            : "Set Plan Amount"
                        }
                      >
                        <AntButton
                          type="link"
                          size="small"
                          icon={<EditOutlined />}
                          onClick={handleEditAmount}
                          style={{
                            color:
                              orderSummary.planAmount > 0
                                ? undefined
                                : "#ff4d4f",
                          }}
                        />
                      </Tooltip>
                    </Space>
                  )}
                </Col>
              </Row>

              {/* Final Amount Calculation Display */}
              <Card
                size="small"
                className="mb-3"
                style={{
                  background:
                    "linear-gradient(135deg, #fff7e6 0%, #ffe7ba 100%)",
                  border: "2px solid #ffa940",
                  borderRadius: 8,
                }}
              >
                <Row justify="space-between" align="middle">
                  <Col span={14}>
                    <Text strong style={{ fontSize: 14, color: "#d46b08" }}>
                      Final Amount
                    </Text>
                    <br />
                    <Text
                      type="secondary"
                      style={{ fontSize: 11, color: "#8c8c8c" }}
                    >
                      ₹{money(orderSummary.planAmount)}{" "}
                      × {orderSummary.totalTMCount} TMs
                    </Text>
                  </Col>
                  <Col span={10} className="text-end">
                    <motion.div
                      key={orderSummary.finalAmount}
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Text
                        strong
                        style={{
                          fontSize: 20,
                          color: "#fa8c16",
                          display: "block",
                          lineHeight: 1.2,
                        }}
                      >
                        ₹
                        {money(orderSummary.finalAmount)}
                      </Text>
                    </motion.div>
                  </Col>
                </Row>
              </Card>

              {/* GST Checkbox and Amount */}
              <motion.div
                whileHover={{ scale: 1.01 }}
                transition={{ duration: 0.2 }}
              >
                <Row
                  justify="space-between"
                  align="middle"
                  className="mb-3 p-3"
                  style={{
                    background: formData.gst_inclusion
                      ? "linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)"
                      : "linear-gradient(135deg, #fafafa 0%, #f0f0f0 100%)",
                    borderRadius: 10,
                    border: formData.gst_inclusion
                      ? "2px solid #52c41a"
                      : "2px solid #d9d9d9",
                    boxShadow: formData.gst_inclusion
                      ? "0 2px 8px rgba(82, 196, 26, 0.15)"
                      : "none",
                    transition: "all 0.3s ease",
                  }}
                >
                  <Col>
                    <Space>
                      {/* Shows what is intended, not what the rate has managed
                          to load. Gating the tick on the rate meant a slow or
                          failed /tax-rates left GST reading as switched off,
                          which is a different claim from "on, amount pending" —
                          and the label below says which of the two it is.
                          Still not editable until the rate is known: a tax
                          cannot be charged at a rate nobody has. */}
                      <Checkbox
                        checked={Boolean(formData.gst_inclusion)}
                        onChange={handleGSTCheckboxChange}
                        disabled={!gst.ready}
                      />
                      <div>
                        <Text strong style={{ fontSize: 13 }}>
                          GST {gst.ready ? `(${gst.percent}%)` : ""}
                        </Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: 10 }}>
                          {gst.ready
                            ? "Tax on final amount"
                            : gst.isLoading ? "Loading the current rate…" : "Rate unavailable"}
                        </Text>
                      </div>
                    </Space>
                  </Col>
                  <Col>
                    <motion.div
                      key={
                        formData.gst_inclusion ? "gst-included" : "gst-excluded"
                      }
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Text
                        strong
                        style={{
                          fontSize: 18,
                          color: formData.gst_inclusion ? "#52c41a" : "#8c8c8c",
                          textDecoration: formData.gst_inclusion
                            ? "none"
                            : "line-through",
                        }}
                      >
                        ₹
                        {formData.gst_inclusion
                          ? money(orderSummary.gstAmount)
                          : "0.00"}
                      </Text>
                    </motion.div>
                  </Col>
                </Row>
              </motion.div>

              <Divider style={{ margin: "12px 0" }} />

              {/* Total Payable */}
              <motion.div
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
              >
                <div
                  className="p-3 rounded mt-3"
                  style={{
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    boxShadow: "0 6px 20px rgba(102, 126, 234, 0.4)",
                    borderRadius: 12,
                  }}
                >
                  <Row justify="space-between" align="middle">
                    <Col>
                      <Text
                        strong
                        className="text-white"
                        style={{ fontSize: 15 }}
                      >
                        Total Payable
                      </Text>
                      <Text
                        className="text-white d-block"
                        style={{ fontSize: 11, opacity: 0.9 }}
                      >
                        {formData.gst_inclusion
                          ? "(incl. GST)"
                          : "(without GST)"}
                      </Text>
                    </Col>
                    <Col>
                      <motion.div
                        key={`${orderSummary.totalAmount}-${orderSummary.includeGST}`}
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{
                          type: "spring",
                          stiffness: 260,
                          damping: 20,
                        }}
                      >
                        <Text
                          strong
                          className="text-white"
                          style={{
                            fontSize: 26,
                            letterSpacing: 0.5,
                            textShadow: "0 2px 4px rgba(0,0,0,0.2)",
                          }}
                        >
                          ₹
                          {money(orderSummary.totalAmount)}
                        </Text>
                      </motion.div>
                    </Col>
                  </Row>
                </div>
              </motion.div>
            </div>
          )}

          {/* Submit Button */}
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <AntButton
              type="primary"
              size="large"
              block
              icon={<PlusOutlined />}
              onClick={() => formRef.current?.requestSubmit()}
              loading={isSubmitting}
              disabled={
                isSubmitting ||
                !formData.user_id ||
                !formData.plan_id ||
                orderSummary.customerName === "-"
              }
              className="mt-3"
              style={{
                height: 52,
                fontSize: 16,
                fontWeight: 600,
                background:
                  isSubmitting || !formData.user_id || !formData.plan_id
                    ? "#d9d9d9"
                    : "linear-gradient(135deg, #52c41a 0%, #73d13d 100%)",
                borderColor: "transparent",
                boxShadow: "0 4px 12px rgba(82, 196, 26, 0.4)",
                borderRadius: 8,
              }}
            >
              {isSubmitting
                ? isEditing
                  ? "Updating Subscription..."
                  : "Creating Subscription..."
                : isEditing
                  ? "Update Subscription"
                  : "Create Subscription"}
            </AntButton>
          </motion.div>

          {/* Helper Text */}
          {!formData.user_id && (
            <Text
              type="secondary"
              className="d-block text-center mt-2"
              style={{ fontSize: 11 }}
            >
              Please select customer to enable subscription creation
            </Text>
          )}
        </div>
      </Card>
    </motion.div>
  );
};

export default OrderSummary;
